import { useState } from 'react';
import { X, Save, Plus, Pencil } from 'lucide-react';
import apiClient from '../api/client';
import JsonEditor from './JsonEditor';

export interface WorkflowItem {
  id: string;
  name: string;
  description: string | null;
  entry_state: string;
  states: Record<string, unknown>;
  exceptions: unknown[] | null;
  global_vars: Record<string, unknown> | null;
  is_enabled: boolean;
}

interface WorkflowEditorProps {
  workflow?: WorkflowItem | null;
  profileId: string;
  onSave: () => void;
  onCancel: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
};

const modalStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-lg)',
  width: '90%',
  maxWidth: 680,
  maxHeight: '90vh',
  overflow: 'auto',
  padding: 24,
};

// 从 axios 错误中提取后端 detail 文案
function apiErrorDetail(e: unknown): string | null {
  const err = e as { response?: { data?: { detail?: unknown } } };
  return typeof err.response?.data?.detail === 'string' ? err.response.data.detail : null;
}

export default function WorkflowEditor({ workflow, profileId, onSave, onCancel }: WorkflowEditorProps) {
  const isEdit = !!workflow;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [entryState, setEntryState] = useState('');
  const [states, setStates] = useState<object>({});
  const [exceptions, setExceptions] = useState<unknown[]>([]);
  const [globalVars, setGlobalVars] = useState<object>({});
  const [isEnabled, setIsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // 渲染期调整：workflow prop 变化时同步表单（替代原 useEffect）
  const [prevWorkflow, setPrevWorkflow] = useState(workflow);
  if (prevWorkflow !== workflow) {
    setPrevWorkflow(workflow);
    if (workflow) {
      setName(workflow.name);
      setDescription(workflow.description || '');
      setEntryState(workflow.entry_state);
      setStates(workflow.states || {});
      setExceptions(workflow.exceptions || []);
      setGlobalVars(workflow.global_vars || {});
      setIsEnabled(workflow.is_enabled);
    } else {
      setName('');
      setDescription('');
      setEntryState('');
      setStates({});
      setExceptions([]);
      setGlobalVars({});
      setIsEnabled(true);
    }
    setFormError(null);
  }

  const validateStates = (obj: object): string | null => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return 'states 必须是对象';
    if (Object.keys(obj).length === 0) return 'states 至少需要一个状态';
    return null;
  };

  const handleSubmit = async () => {
    if (!name.trim() || !entryState.trim()) {
      setFormError('名称和入口状态为必填');
      return;
    }
    const statesError = validateStates(states);
    if (statesError) {
      setFormError(`states 校验失败: ${statesError}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        entry_state: entryState.trim(),
        states,
        exceptions: exceptions.length > 0 ? exceptions : null,
        global_vars: Object.keys(globalVars).length > 0 ? globalVars : null,
        is_enabled: isEnabled,
      };
      if (isEdit && workflow) {
        await apiClient.put(`/profiles/${profileId}/workflows/${workflow.id}`, payload);
      } else {
        await apiClient.post(`/profiles/${profileId}/workflows`, payload);
      }
      onSave();
    } catch (e) {
      setFormError(apiErrorDetail(e) || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 style={{ margin: 0 }}>
            {isEdit ? <><Pencil size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />编辑工作流</> : <><Plus size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />新增工作流</>}
          </h3>
          <button className="secondary" style={{ padding: '6px 8px' }} onClick={onCancel}>
            <X size={16} />
          </button>
        </div>

        {formError && (
          <div className="card mb-4" style={{ borderColor: 'var(--color-error)', background: 'rgba(248,81,73,0.1)' }}>
            <p style={{ color: 'var(--color-error)', margin: 0, fontSize: 13 }}>{formError}</p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
              名称 <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="如：减重咨询流程"
            />
          </div>

          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>描述</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="工作流功能描述"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
                入口状态 <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <input
                value={entryState}
                onChange={(e) => setEntryState(e.target.value)}
                placeholder="如：assess"
              />
            </div>
            <div className="flex items-center" style={{ paddingTop: 24 }}>
              <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                  style={{ width: 'auto' }}
                />
                <span className="text-sm text-muted">启用</span>
              </label>
            </div>
          </div>

          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
              States (状态机定义) <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <JsonEditor
              value={states}
              onChange={setStates}
              rows={10}
              placeholder={`{"assess": {"name": "评估", "transitions": [...]}, "plan": {...}}`}
              validate={validateStates}
            />
          </div>

          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Exceptions (异常处理)</label>
            <JsonEditor
              value={exceptions}
              onChange={(v) => setExceptions(Array.isArray(v) ? v : [])}
              rows={5}
              placeholder={`[{"trigger": "...", "action": "...", "next": "..."}]`}
            />
          </div>

          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Global Vars (全局变量)</label>
            <JsonEditor
              value={globalVars}
              onChange={setGlobalVars}
              rows={4}
              placeholder={`{"timeout": 30, "max_retries": 3}`}
            />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button className="primary" onClick={handleSubmit} disabled={saving} style={{ flex: 1 }}>
            <Save size={14} /> {saving ? '保存中...' : '保存'}
          </button>
          <button className="secondary" onClick={onCancel} disabled={saving}>
            取消
          </button>
        </div>
      </div>
    </div>
  );
}
